import React, { useRef, useEffect } from 'react';
import { useSelector, useStore } from 'react-redux';
import {
  fetchMoreOp, selectItemsListIsLoaded,
  selectItemsListIsLoadingMore,
  selectItemsListPageStops,
  selectItemsListParams,
  selectItemsListState,
  selectNoMoreItems,
  selectShowMedia,
  selectShowNsfw,
  selectSearchDefaultSort,
  selectSearchAvailableSorting,
  changeSearchDefaultSort,
  selectFloppersDefaultSort,
  selectFloppersAvailableSorting,
  changeFloppersDefaultSort,
} from '../../ducks';
import { Dropdown, ClickElement } from '../form';
import styles from './ItemsList.css';
import { isDateToppers, isToppers, isDateNew, isDatePage, ListType } from '../../apis/dumpert';
import { Loading } from '../loading';
import { Filter } from '../filter';
import {
  formatDate,
  isElementInViewport,
  mutateDate,
  typeOfListToType,
  adBreakPoint,
} from '../../lib/helpers';
import c from 'classnames';
import { ItemsList } from './ItemsList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Pagination } from '../pagination';
import { ITEMSLIST, MENU, FILTER } from '../../lib/labels';
import { useChangeMediaFilter, useCurrentBreakPoint } from '../commons';
import fapvoerImg from './fapvoer.jpg';

const dateToppersPreviousPageTitle = type => ({
  day: ITEMSLIST.LAZYLOAD.TITLE.YESTERDAY,
  week: ITEMSLIST.LAZYLOAD.TITLE.PREVIOUS_WEEK,
  month: ITEMSLIST.LAZYLOAD.TITLE.PREVIOUS_MONTH,
}[type]);

const getDayTitle = date => date.toLocaleString('nl-NL', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
}).replace('.', '');

const dateToppersFormatPageTitle = (date, type) => {
  switch (type) {
  case 'day':
    return getDayTitle(date);
  case 'week':
    date.setDate(date.getDate() - date.getDay());
    return `WEEK VAN ${getDayTitle(date)}`;
  case 'month':
    return date.toLocaleString('nl-NL', { month: 'long', year: 'numeric' });
  }
};

const DatePageTitle = ({ offset }) => {
  if (offset) {
    const day = offset.split('-')[0];
    const month = Number(offset.split('-')[1]) - 1;
    const year = offset.split('-')[2];
    const setDate = new Date(Date.UTC(year, month, day));
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return setDate.toLocaleDateString('nl-NL', options);
  }
};

const getDateToppersPageTitles = (pageStops, type, date) =>
  [...Array(Math.max(pageStops.length - 1, 0)).keys()]
    .reduce((a, i) => ({
      ...a,
      [pageStops[i]]: i ?
        dateToppersFormatPageTitle(mutateDate(new Date(date.getTime()), type, pageStops.length - 2 - i), type) :
        dateToppersPreviousPageTitle(type),
    }), {});


export const ItemsListLazyLoad = ({ typeOfList, params, emptySearch }) => {

  const { tagId, offset, pageNum, listType } = params;

  const { dispatch, getState } = useStore();
  const dateType = typeOfListToType(typeOfList);
  const isDateTopper = isDateToppers(typeOfList);
  const isDateNewPage = isDateNew(typeOfList);
  const isDateRequest = isDatePage(typeOfList);
  const lazyLoader = useRef(null);
  const isLoadingMore = useSelector(state => selectItemsListIsLoadingMore(state, typeOfList));
  const noMoreItems = useSelector(state => selectNoMoreItems(state, typeOfList));
  const pageStops = useSelector(state => selectItemsListPageStops(state, typeOfList));
  const searchDefaultSort = useSelector(selectSearchDefaultSort);
  const searchAvailableSorting = useSelector(selectSearchAvailableSorting);
  const listParams = useSelector(state => selectItemsListParams(state, listType));
  const showNsfw = useSelector(selectShowNsfw);
  const { isLoading, isLoaded, error, items } = useSelector(state => selectItemsListState(state, typeOfList));
  const currentDate = mutateDate(new Date(), dateType, -Math.max(pageStops.length - 1, 0));
  const isTagList = typeOfList === ListType.tag;
  const isSearchList = typeOfList === ListType.zoek;
  const showMedia = useSelector(selectShowMedia);
  const currentBreakPoint = useCurrentBreakPoint();
  const pages = isDateTopper && items.length > 0 ?
    getDateToppersPageTitles(pageStops, dateType, currentDate) : undefined;
  const changeMediaFilter = useChangeMediaFilter();
  const floppersDefaultSort = useSelector(selectFloppersDefaultSort);
  const floppersAvailableSorting = useSelector(selectFloppersAvailableSorting);
  const isFloppersList = typeOfList === 'floppers';
  const dropDownProps = {
    defaultSort: isFloppersList ? floppersDefaultSort : searchDefaultSort,
    availableSorting: isFloppersList ? floppersAvailableSorting : searchAvailableSorting,
    changeSorting: isFloppersList ? changeFloppersDefaultSort : changeSearchDefaultSort,
    setCookieValue: isFloppersList ? 'floppersDefaultSort' : 'searchDefaultSort',
  };

  const NewPageDateTitle = ({ offset }) => {
    const number = Number(offset) || 0;
    let date = '';
    if (number === 0 || number === undefined) { return ITEMSLIST.PAGINATED.TITLE.NEW_TODAY; }
    if (number % 7 === 0) {
      date = `${number / 7} ${number / 7 === 1
        ? ITEMSLIST.PAGINATED.TITLE.NEW_WEEK
        : ITEMSLIST.PAGINATED.TITLE.NEW_WEEKS}`;
    }
    if (number % 7 !== 0) {
      date = `${number} ${number === 1
        ? ITEMSLIST.PAGINATED.TITLE.NEW_DAY
        : ITEMSLIST.PAGINATED.TITLE.NEW_DAYS}`;
    }
    return `${date} ${ITEMSLIST.PAGINATED.TITLE.NEW_AGO}`;
  };

  const listProps = {
    hideAds: false, // enable dynamic ads
    items,
    pages,
    isToppers: isToppers(typeOfList),
    isSidebar: false,
    typeOfList: typeOfList,
  };

  const titles = {
    [ListType.latest]: FILTER.NEW,
    [ListType.toppers]: FILTER.TOP,
    [ListType.top_dag]: ITEMSLIST.LAZYLOAD.TITLE.TOPPERS_TODAY,
    [ListType.top_week]: ITEMSLIST.LAZYLOAD.TITLE.TOPPERS_WEEK,
    [ListType.top_maand]: ITEMSLIST.LAZYLOAD.TITLE.TOPPERS_MONTH,
    [ListType.floppers]: MENU.ITEM.FLOPPERS,
  };

  const PaginationHolder = ({ offset, pathPrefix }) => <Pagination
    offset={offset}
    pathPrefix={pathPrefix}
  />;

  const onScroll = event => {
    const { current } = lazyLoader;
    if (!current) {
      return;
    }
    if (isElementInViewport(current) || event.type === 'click') {
      const state = getState();
      if (!selectItemsListIsLoaded(state, typeOfList) || selectNoMoreItems(state, typeOfList)) {
        return;
      }
      if (isDateTopper) {
        mutateDate(currentDate, dateType, -1);
        dispatch(fetchMoreOp(
          typeOfList,
          {
            ...params,
            pageNum: formatDate(currentDate, dateType),
          },
        ));
      } else {
        if (isSearchList || isTagList) {
          dispatch(fetchMoreOp(typeOfList, selectItemsListParams(state, typeOfList)));
        } else {
          dispatch(fetchMoreOp(typeOfList, params));
        }
      }
    }
  };

  useEffect(() => {
    const listParamsUpdated = () =>
      ['listType', 'tagId'].some(k => listParams[k] !== params[k]);
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (listParamsUpdated()) {
        window.scrollTo(0, 0);
      }
    };
  }, [listType, tagId, listParams.listType, listParams.tagId]);

  // EMPTY SEARCH RESULTS
  if ((isLoaded && items.length === 0 && !isDateNewPage && !isDateRequest) || emptySearch) {
    return (
      <div className={c(
        styles.noresult,
        { [styles.taglist]: isSearchList },
        'noresult',
        { ['taglist']: isSearchList },
      )}>
        {isSearchList && (
          <>
            <div className={c(
              'full',
              styles.list_bar,
            )}>
              <div className={c(
                'list_title_holder',
                styles.list_bar_left,
              )}>
                <h1>
                  {emptySearch && (<>{ITEMSLIST.LAZYLOAD.SEARCH.NO_RESULT.EMPTY}</>)}
                  {(isSearchList && !emptySearch) && (
                    <>{ITEMSLIST.LAZYLOAD.SEARCH.NO_RESULT.TITLE} {tagId}</>
                  )}
                </h1>
                {!emptySearch && (
                  <p>
                    <br />
                    {ITEMSLIST.LAZYLOAD.SEARCH.NO_RESULT.CONTENT} <Link to="/zoek/mand">Mand!</Link>
                  </p>
                )}
              </div>
            </div>
          </>
        )}
        {!isSearchList && <>
          <div className={c(
            'full',
            styles.list_bar,
          )}>
            <p>
              <br/>
              <h2>{ITEMSLIST.LAZYLOAD.NO_RESULT}</h2>
            </p>
          </div>
        </>}
      </div>
    );
  }

  // PAGINATION PAGE
  return (
    <>
      {error && <></>}
      <div className={c(
        'grid',
        { ['one']: ['XS'].includes(adBreakPoint(currentBreakPoint)) },
        { ['two']: ['S', 'M'].includes(adBreakPoint(currentBreakPoint)) },
        { ['three']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) },
      )}>
        {(isTagList && tagId === 'fap' && showNsfw) && (
          <div className={c(
            'full',
          )}>
            <img src={fapvoerImg} alt="fapvoer" width="100%" />
          </div>
        )}
        <div className={c(
          'full',
        )}>
          <div className={c(
            styles.list_bar,
          )}>
            <div className={c(
              'list_title_holder',
              styles.list_bar_left,
            )}>
              <h1>
                {(tagId) && (<>{tagId === 'dumperttv' ? tagId : `${ITEMSLIST.LAZYLOAD.SEARCH.TITLE} '${tagId}'`}</>)}
                {!(tagId) && (<>{titles[typeOfList]}</>)}
                {isDateNewPage && <NewPageDateTitle offset={offset} />}
                {isDateRequest && <DatePageTitle offset={pageNum} />}
              </h1>
            </div>
            <div className={c(
              styles.list_bar_right,
              { ['search_dropdown']: isSearchList || isTagList },
            )}>
              {/* Set filter for latest (mobile) / DumpertTV (mobile) / toppers / datetopppers  */}
              {(
                (typeOfList === 'latest' && ['XS'].includes(adBreakPoint(currentBreakPoint))) ||
                (isTagList && tagId === 'dumperttv' && ['XS'].includes(adBreakPoint(currentBreakPoint))) ||
                isDateToppers(typeOfList) ||
                isToppers(typeOfList)
              ) && (
                <Filter typeOfList={typeOfList} isItemViewPage={false} tagId={tagId} />
              )}
              {/* Set refresh button for latest list on desktop  */}
              {(typeOfList === 'latest') && (
                <ClickElement
                  tertiary
                  ElementType="button"
                  type="button"
                  text={(<><FontAwesomeIcon icon="sync-alt" size="lg" /> <span>Herladen</span></>)}
                  disabled={isLoading}
                  onClick={() => { window.location.reload(); }}
                />
              )}
              {/* Set pagination for /nieuw/ route on top */}
              {isDateNewPage && <PaginationHolder offset={Number(offset) || 0} pathPrefix={'/nieuw/dag'} />}

              {/* Set sorting searchlist  */}
              {(isSearchList || isTagList || isFloppersList) && (
                <>
                  <div className={styles['dropdown-label']}>
                    <span className='text-color'>{ITEMSLIST.LAZYLOAD.SEARCH.SORTED_ON}</span>
                  </div>
                  <Dropdown
                    defaultSort={dropDownProps.defaultSort}
                    availableSorting={dropDownProps.availableSorting}
                    changeSorting={dropDownProps.changeSorting}
                    setCookieValue={dropDownProps.setCookieValue}
                  />
                </>
              )}
            </div>
          </div>
          {/* Show media filter when video / foto media is selected  */}
          {(showMedia && !isTagList && !isSearchList) && (
            <div className={styles.filtered_bar}>
              {`filter: ${showMedia.replace('/', '')}`}
            &nbsp;-&nbsp;
              <div onClick={() => changeMediaFilter('')}>verwijder</div>
            </div>

          )}
        </div>

        {/* Show no results text when 0 items are returned */}
        {((isDateNewPage || isDateRequest) && isLoaded && items.length === 0) && (
          <div className={'full'}>
            <div style={{ 'padding': '130px 2rem 127px' }}>
              <h1 style={{ 'fontSize': '2em' }}>{ITEMSLIST.PAGINATED.NO_ITEMS}</h1>
            </div>
          </div>
        )}

        {/* Gridlist with items */}
        {isLoading && (
          <div className={'full'}>
            <Loading whatIsLoading="items" />
          </div>
        )}
        {(isLoaded || isLoadingMore) && (
          <ItemsList {...listProps} />
        )}

        {/* Set pagination for /nieuw/ route on bottom of list  */}
        {isDateNewPage && (
          <div className={c(
            'full',
            styles.list_bar,
          )}>
            <div className={c(
              'list_title_holder',
              styles.list_bar_left,
            )}>
            </div>
            <div className={c(
              styles.list_bar_right,
            )}>
              <PaginationHolder offset={Number(offset) || 0} pathPrefix={'/nieuw/dag'} />
            </div>
          </div>
        )}

      </div>
      {/* Add lazyload scroll button */}
      {((!isDateNewPage && !isDateRequest) && (isLoaded || isLoadingMore)) && (
        <>
          {noMoreItems && <div className={'nomoreitems'}>
            <div className={'textbar'}>
              {ITEMSLIST.LAZYLOAD.NO_MORE_ITEMS} <FontAwesomeIcon icon={['fas', 'check']} />
            </div>
          </div>}
          {!noMoreItems && (
            <>
              <div className={c(
                'scrollmoreitems',
                { ['hide']: isLoadingMore },
              )} onClick={e => onScroll(e)}>
                <div className={'textbar'}>
                  {ITEMSLIST.LAZYLOAD.SCROLL_MORE_ITEMS}
                  <div className={styles.arrow_animation}>
                    <FontAwesomeIcon icon={['fas', 'angle-double-down']} />
                  </div>
                </div>
              </div>
              <div className={c(
                styles.loadmoreitems,
                { ['loadingmore']: isLoadingMore })
              } ref={lazyLoader}>
                <div className={'textbar'}>
                  {ITEMSLIST.LAZYLOAD.LOADING_MORE_ITEMS} <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

